import { Http, baseApi, rtkq, RtkqRequest, openid, asky } from '@app/base';
import { Role } from './usersTypes';
import {
  Employee,
  EmployeeListItem,
  EmployeeLookup,
  EmployeeOverview,
  InviteEmployeeRequest,
  SignUpProviderRequest,
  UpdateEmployeeProfileRequest,
  UpdateEmployeeRequest,
} from './employeesTypes';
import {
  GetEmployeeDocument,
  GetEmployeeListDocument,
  GetEmployeeLookupsDocument,
  GetEmployeeOverviewDocument,
} from './graphql';
import { selectContext } from './usersApi';

export type UpdateEmployeeProfileArgs = UpdateEmployeeProfileRequest & {
  id: string;
};

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const employeesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getEmployeeProfile: build.query<Employee, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetEmployeeDocument, {
        select: (x) => x.employee,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (employee) => [
        { type: 'main:employee', id: employee?.id ?? 'none' },
        { type: 'main:user', id: employee?.id ?? 'none' },
      ],
    }),

    updateEmployeeProfile: build.mutation<void, RtkqRequest<UpdateEmployeeProfileArgs>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/employee/profile`, args)),
      invalidatesTags: (_, __, args) => [
        { type: 'main:employee', id: args.id },
        { type: 'main:user', id: args.id },
      ],
    }),

    getEmployees: build.query<EmployeeListItem[], RtkqRequest<void>>({
      queryFn: rtkq.graphql(GetEmployeeListDocument, (x) => x.employees),
      providesTags: (result = []) => [
        { type: 'main:employee', id: 'list' },
        { type: 'main:user', id: 'list' },
        ...result.flatMap(({ id }) => [
          { type: 'main:employee' as const, id },
          { type: 'main:user' as const, id },
        ]),
      ],
    }),

    getEmployeeLookups: build.query<EmployeeLookup[], RtkqRequest<{ role?: Role; onlyActive?: boolean }>>({
      queryFn: rtkq.graphql(GetEmployeeLookupsDocument, {
        select: (x) => x.employees,
        vars: ({ role, onlyActive }) => ({
          filter: JSON.stringify(
            asky.filter.create([
              !!onlyActive && { fieldId: 'active', operator: '=', value: true },
              !!role && { fieldId: 'role', operator: 'contains', value: role },
            ]),
          ),
        }),
      }),
      providesTags: (result = []) => [
        { type: 'main:employee', id: 'list' },
        { type: 'main:user', id: 'list' },
        ...result.flatMap(({ id }) => [
          { type: 'main:employee' as const, id },
          { type: 'main:user' as const, id },
        ]),
      ],
    }),

    getEmployeeLookupsByRoles: build.query<EmployeeLookup[], RtkqRequest<{ roles?: Role[]; onlyActive?: boolean }>>({
      queryFn: rtkq.graphql(GetEmployeeLookupsDocument, {
        select: (x) => x.employees,
        vars: ({ roles, onlyActive }) => ({
          filter: JSON.stringify(
            asky.filter.create([
              !!onlyActive && { fieldId: 'active', operator: '=', value: true },
              !!roles &&
                (roles.length > 1 ? {
                  operator: 'or',
                  children: roles.map((role) => ({
                    fieldId: 'role',
                    operator: 'contains',
                    value: role,
                  })),
                } : { fieldId: 'role', operator: 'contains', value: roles[0] }),
            ]),
          ),
        }),
      }),
      providesTags: (result = []) => [
        { type: 'main:employee', id: 'list' },
        { type: 'main:user', id: 'list' },
        ...result.flatMap(({ id }) => [
          { type: 'main:employee' as const, id },
          { type: 'main:user' as const, id },
        ]),
      ],
    }),

    getEmployee: build.query<Employee, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetEmployeeDocument, {
        select: (x) => x.employee,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [
        { type: 'main:employee', id },
        { type: 'main:user', id },
      ],
    }),

    getEmployeeOverview: build.query<EmployeeOverview, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetEmployeeOverviewDocument, {
        select: (x) => x.employee!,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [
        { type: 'main:employee', id },
        { type: 'main:user', id },
      ],
    }),

    updateEmployee: build.mutation<void, RtkqRequest<UpdateEmployeeRequest>>({
      queryFn: rtkq.axios(async (args, api) => {
        const result = await Http.put(`/api/employee/${args.id}`, args);

        if (args.id === selectContext(api).id) {
          // we wait for 1s to give a time for the synchronization with identity server
          // because if user updated his own roles, new token might be issued
          // see https://jira.itransition.com/browse/STARTERKIT-969 for details
          await wait(1000);
          await openid.renewRevokedToken();
        }

        return result;
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'main:employee', id },
        { type: 'main:user', id },
      ],
    }),

    inviteEmployee: build.mutation<void, RtkqRequest<InviteEmployeeRequest>>({
      queryFn: rtkq.axios((args) => Http.post(`/api/employee/invite`, args)),
      invalidatesTags: [
        { type: 'main:user', id: 'list' },
        { type: 'main:employee', id: 'list' },
      ],
    }),

    signUpProvider: build.mutation<void, RtkqRequest<SignUpProviderRequest>>({
      queryFn: rtkq.axios((args) => Http.anonymous().post(`/api/employee/sign-up`, args)),
      invalidatesTags: [
        { type: 'main:user', id: 'list' },
        { type: 'main:employee', id: 'list' },
      ],
    }),
  }),
});

export const {
  useGetEmployeeProfileQuery,
  useLazyGetEmployeeProfileQuery,
  useGetEmployeesQuery,
  useLazyGetEmployeeLookupsQuery,
  useGetEmployeeLookupsQuery,
  useGetEmployeeLookupsByRolesQuery,
  useLazyGetEmployeeLookupsByRolesQuery,
  useGetEmployeeQuery,
  useUpdateEmployeeMutation,
  useUpdateEmployeeProfileMutation,
  useInviteEmployeeMutation,
  useSignUpProviderMutation,
  useGetEmployeeOverviewQuery,
} = employeesApi;
